import React, { useMemo, useState } from "react";
import { Box, DialogContent, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ENTITY_ICON } from "../../entityIcons";
import { CatalogEntityType } from "../../types";
import ProgressModalFooter from "../components/ProgressModalFooter";
import StatusForm from "../components/StatusForm";
import XapiStatementsSection from "../components/XapiStatementsSection";
import XapiStatesSection from "../components/XapiStatesSection";
import {
  StatusFormErrors,
  StatusFormFieldKey,
  StatusFormKind,
  StatusFormValues,
  XAPI_REASON,
  clearDisabledValues,
  getStatusFormFields,
  validateStatusForm,
} from "../config/statusFormConfig";
import { collectEditedStates } from "../config/xapiStates";
import { ProgressNode, XapiActivityState, XapiStatement } from "../types";
import { isNodeExempt, summarizeSelection } from "../utils";

const RECAP_ORDER: CatalogEntityType[] = ["path", "course", "activity"];

interface ChangeStatusViewProps {
  kind: StatusFormKind;
  selection: ProgressNode[];
  initialValues: StatusFormValues;
  statements?: XapiStatement[];
  activityStates?: XapiActivityState[];
  isSubmitting?: boolean;
  onBack: () => void;
  onConfirm?: (
    values: StatusFormValues,
    editedStates?: XapiActivityState[],
  ) => void;
}

const ChangeStatusView: React.FC<ChangeStatusViewProps> = ({
  kind,
  selection,
  initialValues,
  statements = [],
  activityStates = [],
  isSubmitting,
  onBack,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const withScore = selection.length === 1 && selection[0].isTest;
  const hasExemptSelected = selection.some(isNodeExempt);
  const fields = useMemo(
    () => getStatusFormFields(kind, { withScore }),
    [kind, withScore],
  );
  const [values, setValues] = useState<StatusFormValues>(initialValues);
  const [errors, setErrors] = useState<StatusFormErrors>({});
  const [stateDrafts, setStateDrafts] = useState<Record<string, string>>({});
  const [invalidStateIds, setInvalidStateIds] = useState<string[]>([]);
  const summary = summarizeSelection(selection);
  const isXapi = kind === "xapi";
  const isLocked = isSubmitting || !onConfirm;
  const allFields = useMemo(
    () => (isXapi ? [...fields, XAPI_REASON] : fields),
    [fields, isXapi],
  );

  const changeValue = (key: StatusFormFieldKey, value: string | null) => {
    setValues((prev) =>
      clearDisabledValues(
        fields,
        { ...prev, [key]: value },
        initialValues,
        key,
      ),
    );
    setErrors((prev) => ({ ...prev, [key]: false }));
  };

  const confirm = () => {
    const nextErrors = validateStatusForm(allFields, values);
    setErrors(nextErrors);
    const edited = isXapi
      ? collectEditedStates(activityStates, stateDrafts)
      : { states: [], invalidIds: [] };
    setInvalidStateIds(edited.invalidIds);
    if (
      Object.keys(nextErrors).length === 0 &&
      edited.invalidIds.length === 0
    ) {
      onConfirm?.(values, isXapi ? edited.states : undefined);
    }
  };

  const recapSx = { fontSize: "12px", fontWeight: 700 };

  return (
    <>
      <DialogContent sx={{ padding: "12px 24px", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
            color: theme.palette.text.primary,
            "& svg": { width: 14, height: 14 },
          }}
        >
          <Typography sx={{ fontSize: "12px" }}>{t("form.recap")}</Typography>
          {kind === "generic" ? (
            RECAP_ORDER.filter((type) => summary[type] > 0).map((type) => (
              <Box
                key={type}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {ENTITY_ICON[type]}
                <Typography sx={recapSx}>
                  {t(`recap.${type}`, { count: summary[type] })}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={recapSx}>
              {t("recap.tracking", {
                kind: t(`tracking.kind.${kind === "xapi" ? "xapi" : "scorm"}`),
              })}
            </Typography>
          )}
        </Box>

        <StatusForm
          kind={kind}
          fields={fields}
          values={values}
          errors={errors}
          disabled={isLocked}
          hasExemptSelected={hasExemptSelected}
          onChange={changeValue}
        />

        {isXapi && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 3 }}>
            <XapiStatementsSection statements={statements} />
            <XapiStatesSection
              states={activityStates}
              drafts={stateDrafts}
              invalidIds={invalidStateIds}
              disabled={isLocked}
              onChange={(id, text) => {
                setStateDrafts((prev) => ({ ...prev, [id]: text }));
                setInvalidStateIds((prev) =>
                  prev.filter((item) => item !== id),
                );
              }}
            />
            <StatusForm
              kind={kind}
              fields={[XAPI_REASON]}
              values={values}
              errors={errors}
              disabled={isLocked}
              onChange={changeValue}
            />
          </Box>
        )}
      </DialogContent>

      <ProgressModalFooter
        left={
          <ButtonSecondary size="md" onClick={onBack} disabled={isSubmitting}>
            {t("actions.back")}
          </ButtonSecondary>
        }
        right={
          <ButtonPrimary
            size="md"
            onClick={confirm}
            disabled={!onConfirm || isSubmitting}
          >
            {t("actions.confirm")}
          </ButtonPrimary>
        }
      />
    </>
  );
};

export default ChangeStatusView;
